import CancelScheduleSendOutlinedIcon from "@mui/icons-material/CancelScheduleSendOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditNoteOutlinedIcon from "@mui/icons-material/EditNoteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ReplayOutlinedIcon from "@mui/icons-material/ReplayOutlined";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from "@mui/material";
import PaymentReceiptChips from "components/Payments/PaymentReceiptChips";
import { formatDisplayDateTime } from "utils/dateFormat";
import { canChangePendingPayment } from "utils/pendingPaymentChange";
import { STATUS_CHIP } from "./modulePaymentsConstants";
import { isRefund } from "./modulePaymentsLogic";

const Action = ({ title, color, onClick, children }) => (
  <Tooltip title={title}>
    <IconButton size="small" color={color} onClick={onClick} aria-label={title}>
      {children}
    </IconButton>
  </Tooltip>
);

const AuditTrail = ({ logs }) => (
  <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
    {logs.map((log, i) => (
      <Typography key={i} variant="caption" color="text.secondary" display="block">
        <strong>{log.action}</strong>
        {log.actorName ? ` by ${log.actorName}` : ""}
        {log.timestamp ? ` · ${formatDisplayDateTime(log.timestamp)}` : ""}
        {log.details ? `: ${log.details}` : ""}
      </Typography>
    ))}
  </Box>
);

const PaymentRow = ({ payment, rights, onEditPending, onWithdraw, onResubmit, onAction }) => {
  const chip = STATUS_CHIP[payment.status] || { label: payment.status, color: "default" };
  const refund = isRefund(payment);
  const verified = payment.status === "VERIFIED";
  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack direction="row" justifyContent="space-between" spacing={2} flexWrap="wrap">
        <Box className="min-w-0">
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="subtitle2" fontWeight={700}>
              {refund ? "-" : ""}${Number(payment.amount || 0).toFixed(2)}
            </Typography>
            <Chip label={chip.label} size="small" color={chip.color} />
            {refund && <Chip label="Refund" size="small" variant="outlined" color="warning" />}
            {payment.method && <Chip label={payment.method} size="small" variant="outlined" />}
            {payment.date && (
              <Typography variant="caption" color="text.secondary">
                {payment.date}
              </Typography>
            )}
          </Stack>
          {payment.reference && (
            <Typography variant="body2" color="text.secondary">
              Ref: {payment.reference}
            </Typography>
          )}
          {payment.notes && (
            <Typography variant="body2" sx={{ mt: 0.5 }}>
              {payment.notes}
            </Typography>
          )}
          {payment.status === "REJECTED" && payment.rejectionFeedback && (
            <Typography variant="body2" color="error.main" sx={{ mt: 0.5 }}>
              Rejected: {payment.rejectionFeedback}
            </Typography>
          )}
          {payment.submittedByName && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
              Submitted by {payment.submittedByName}
              {payment.verifiedByName ? ` · Verified by ${payment.verifiedByName}` : ""}
              {payment.status === "PENDING_VERIFICATION" && payment.lastEditedByName
                ? ` · Edited by ${payment.lastEditedByName}`
                : ""}
            </Typography>
          )}
          <PaymentReceiptChips urls={payment.attachments || []} />
        </Box>
        <Stack direction="row" spacing={0.5}>
          {canChangePendingPayment(payment) && (
            <>
              <Action title="Edit before it is verified" onClick={() => onEditPending(payment)}>
                <EditNoteOutlinedIcon fontSize="small" />
              </Action>
              <Action
                title="Withdraw from verification"
                color="error"
                onClick={() => onWithdraw(payment)}
              >
                <CancelScheduleSendOutlinedIcon fontSize="small" />
              </Action>
            </>
          )}
          {payment.status === "REJECTED" && (
            <Action title="Resubmit for verification" onClick={() => onResubmit(payment)}>
              <ReplayOutlinedIcon fontSize="small" />
            </Action>
          )}
          {verified && !refund && rights.canModify && (
            <Action title="Modify" onClick={() => onAction("modify", payment)}>
              <EditOutlinedIcon fontSize="small" />
            </Action>
          )}
          {verified && !refund && rights.canRefund && (
            <Action title="Refund" onClick={() => onAction("refund", payment)}>
              <UndoOutlinedIcon fontSize="small" />
            </Action>
          )}
          {verified && rights.canDelete && (
            <Action title="Delete" color="error" onClick={() => onAction("delete", payment)}>
              <DeleteOutlineIcon fontSize="small" />
            </Action>
          )}
        </Stack>
      </Stack>
      {payment.auditLogs?.length > 0 && <AuditTrail logs={payment.auditLogs} />}
    </Paper>
  );
};

export default PaymentRow;
